import { Uuid } from '@ftr/contracts/type/shared'
import { startTimeSorter } from '@ftr/foundation'
import { SttFragment, SttSpeaker, SttUtteranceWithContent, UpsertUtteranceSttEdit } from '@ftr/regional-api-contracts'
import { ChronoUnit, Duration, LocalDateTime } from '@js-joda/core'
import { RealTimeSttRemark } from '../types'

export function utterancesToRemarks(
  utterances: SttUtteranceWithContent[],
  speakers: SttSpeaker[],
): RealTimeSttRemark[] {
  const speakerLookup: Record<Uuid, SttSpeaker> = speakers.reduce(
    (acc, speaker) => ({ [speaker.id]: speaker, ...acc }),
    {},
  )

  return utterances.map(utterance => utteranceToRemark(utterance, speakerLookup)).sort(startTimeSorter)
}

/**
 * Mapping function until STT edit API is also updated to use offsets. Since edits are small the performance benefits
 * of offset are negligible so refactoring edits to use offsets would only be done for consistency.
 */
export function remarkFragmentToSttEditFragment(
  fragment: SttFragment,
  utteranceStartTime: LocalDateTime,
): UpsertUtteranceSttEdit['fragments'][number] {
  return {
    content: fragment.content,
    startTime: utteranceStartTime.plus(fragment.offsetMs, ChronoUnit.MILLIS),
    endTime: utteranceStartTime.plus(fragment.offsetMs + fragment.durationMs, ChronoUnit.MILLIS),
  }
}

/**
 * Mapping function until STT edit API is also updated to use offsets. Since edits are small the performance benefits
 * of offset are negligible so refactoring edits to use offsets would only be done for consistency.
 */
export function sttEditFragmentsFromRemark(remark: RealTimeSttRemark): UpsertUtteranceSttEdit['fragments'] {
  return remark.fragments!.map(fragment => remarkFragmentToSttEditFragment(fragment, remark.startTime))
}

/**
 * Mapping function until STT edit API is also updated to use offsets. Since edits are small the performance benefits
 * of offset are negligible so refactoring edits to use offsets would only be done for consistency.
 */
export function sttEditFragmentToRemarkFragment(
  fragment: UpsertUtteranceSttEdit['fragments'][number],
  utteranceStartTime: LocalDateTime,
): SttFragment {
  return {
    content: fragment.content,
    offsetMs: Duration.between(utteranceStartTime, fragment.startTime).toMillis(),
    durationMs: Duration.between(fragment.startTime, fragment.endTime).toMillis(),
  }
}

function utteranceToRemark(
  utterance: SttUtteranceWithContent,
  speakerLookup: Record<Uuid, SttSpeaker>,
): RealTimeSttRemark {
  return {
    id: utterance.id,
    type: 'Remark',
    content: utterance.content,
    speakerId: utterance.sttSpeakerId,
    speakerName: speakerLookup[utterance.sttSpeakerId].name,
    startTime: utterance.startTime,
    endTime: utterance.endTime,
    dialect: utterance.dialect,
    fragments: utterance.fragments,
    liveStatus: 'final',
  }
}
